import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FtoReportFeatureIdentify, FtoReportFeatureRecognizeResult } from '../feature-identify';
import { Emitter, Logger, type Event } from '@patsnap/tahiti-core';
import { FtoReportTaskStatusService } from '../task-status-service';
import { FtoReportTechnicalFeaturesGetApi, type TechnicalFeaturesGetApiResponse } from '../../apis/technical-features-get-api';
import { FtoReportRetrievalResultReportModel } from '../../models/retrieval-result-report-model';

@injectable()
export class FtoReportFeatureIdentifyImpl implements FtoReportFeatureIdentify {
    // eslint-disable-next-line max-params
    constructor(
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(FtoReportTechnicalFeaturesGetApi)
        private readonly _technicalFeaturesGetApi: FtoReportTechnicalFeaturesGetApi,
        @inject(FtoReportTaskStatusService)
        private readonly _noveltyCheckTaskStatusService: FtoReportTaskStatusService,
        @inject(FtoReportRetrievalResultReportModel)
        private readonly _retrievalResultReportModel: FtoReportRetrievalResultReportModel,
    ) { }

    private _onFeatureRecognitionLoadingEmitter: Emitter<boolean> = new Emitter();

    readonly onFeatureRecognitionLoading: Event<boolean> = this._onFeatureRecognitionLoadingEmitter.event;

    async recognize(taskId: string): Promise<FtoReportFeatureRecognizeResult> {
        this._onFeatureRecognitionLoadingEmitter.fire(true);
        const result = await this._pullTechFeatures(taskId)
            .finally(() => {
                this._onFeatureRecognitionLoadingEmitter.fire(false);
            });
        return result;
    }

    stop(): void {
        this._logger.info('特征识别服务.stop');
        this._abortController?.abort();
        // 刷新任务状态
        this._noveltyCheckTaskStatusService.pullTaskStatus();
    }

    private _abortController: AbortController | undefined;

    async _pullTechFeatures(taskId: string): Promise<FtoReportFeatureRecognizeResult> {
        this._abortController = new AbortController();
        const res = await this._technicalFeaturesGetApi.getTechnicalFeatures({
            task_id: taskId,
        }, this._abortController.signal);
        const result = this._convertToFeatureRecognizeResult(res);
        this._updateFeatureRecognizeResultToModel(result);
        return result;
    }

    private _convertToFeatureRecognizeResult(res: TechnicalFeaturesGetApiResponse): FtoReportFeatureRecognizeResult {
        return {
            text: res.text,
            techFeatures: res.features
                .filter(feature => feature?.tech_feature)
                .map((feature) => ({
                    name: feature.tech_feature,
                    reason: feature.reason,
                    keywords: feature.keywords,
                    score: feature.score,
                    selected: feature.is_select,
                    flagged: feature.core,
                    manualAdded: feature.user_add,
                })),
            threshold: res.threshold,
        };
    }

    /**
     * 将技术特征识别结果更新到模型
     */
    private _updateFeatureRecognizeResultToModel(result: FtoReportFeatureRecognizeResult) {
        this._retrievalResultReportModel.update((state) => {
            state.techFeatures = result.techFeatures;
            state.techEssential = result.text;
            state.threshold = result.threshold;
            return state;
        });
    }
}
